// this component is used to show the releveant modal based on the user's state

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import * as Sentry from '@sentry/react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import selectors from '../../redux/selectors';
import {
  COLLECT_PHONE_NUMBER_COOKIE,
  PREMATTER_ID_COOKIE,
} from '../../constants';
import { toast } from 'react-toastify';
import api from '../../redux/api';
import { actions } from '../../redux/slices';
import { useNavigate } from 'react-router-dom';
import { AuthBodyFooter, AuthModalBodyContent } from './auth-modal-body';
import { getAuthBodyTitle, getAuthBodyDescription } from './auth-text';
import { ModalRef, Modal } from '@law-connect/react-components-general/modal';
import { useTranslation } from 'react-i18next';

interface Props {
  showMobile?: boolean;
  showTerms?: boolean;
  prefillTerms?: boolean;
}

export const AuthCallbackWrapper: React.FC<Props> = (props) => {
  const { showMobile, showTerms, prefillTerms } = props;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const modalRef = useRef<ModalRef>(null);

  const user = useAppSelector(selectors.user.getUser());
  const [showVerificationStep, setShowVerificationStep] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(prefillTerms ?? false);
  const [marketingEmails, setMarketingEmails] = useState(false);
  const { t } = useTranslation();

  const verifyCodeAndContinue = useCallback(
    async (phoneNumber?: string, code?: string) => {
      setIsLoading(true);
      const prematterId = window.sessionStorage.getItem(PREMATTER_ID_COOKIE);
      const storedPhoneNumber =
        phoneNumber || localStorage.getItem('pendingPhoneVerification');
      const codeToUse = code || verificationCode;

      // Verify the code (in production, you would validate with a real verification service)
      // For demo purposes, we're accepting any 6-digit code
      if (codeToUse.length !== 6) {
        toast.error(t('auth.enter-valid-code'));
        setIsLoading(false);
        return;
      }

      const result = await api.user.validateVerificationCode({
        phoneNumber: storedPhoneNumber,
        verificationCode: codeToUse,
      });

      if (result.data?.success) {
        // Fetch the user as that has the updated phone number
        dispatch(actions.user.fetch());

        toast.success(t('auth.phone-number-verified'));
      } else {
        toast.error(result.message || t('auth.invalid-code'));
        setIsLoading(false);
        return;
      }

      if (prematterId) {
        try {
          await dispatch(
            actions.prematter.requestQuote({
              id: prematterId,
            })
          ).unwrap();
          toast.success(t('auth.quote-requested'));
        } catch (error) {
          console.error('error', error);
          toast.error(t('auth.unable-to-request-quote'));
          Sentry.captureException(error);
          setIsLoading(false);
          return;
        }

        // Clean up storage
        localStorage.removeItem('pendingPhoneVerification');
        window.sessionStorage.removeItem(COLLECT_PHONE_NUMBER_COOKIE);
        window.sessionStorage.removeItem(PREMATTER_ID_COOKIE);
        setIsLoading(false);

        navigate(`/case/${prematterId}`);
      } else {
        dispatch(actions.user.fetch());

        // Clean up storage
        localStorage.removeItem('pendingPhoneVerification');
        window.sessionStorage.removeItem(COLLECT_PHONE_NUMBER_COOKIE);
        window.sessionStorage.removeItem(PREMATTER_ID_COOKIE);
        setIsLoading(false);

        navigate('/account');
      }
    },
    [dispatch, navigate, t, verificationCode]
  );

  const sendVerificationCode = useCallback(async () => {
    if (!isValidPhoneNumber(phoneNumber)) {
      toast.error(t('toast.invalid-phone-number'));
      return;
    }

    setIsLoading(true);
    // We want to validate the phone number via the server
    const result = await api.user.checkPhoneAccessibility({
      phoneNumber,
    });

    if (!result.data?.accessible) {
      // Since the phone number is not accessible
      // then we are going to "bypass" the verification step (although not entirely)
      await verifyCodeAndContinue(phoneNumber, result.data?.code);
      setIsLoading(false);
      return;
    }

    try {
      localStorage.setItem('pendingPhoneVerification', phoneNumber);

      const result = await api.user.sendVerificationCode({
        phoneNumber,
      });

      if (result.data?.success) {
        setIsLoading(false);
        setShowVerificationStep(true);
        toast.success(t('auth.code-sent'));
      } else {
        setIsLoading(false);
        toast.error(result.message || t('auth.unable-to-send-code'));
      }
    } catch (error) {
      setIsLoading(false);
      toast.error(t('auth.unable-to-send-code'));
      Sentry.captureException(error);
    }
  }, [phoneNumber, t, verifyCodeAndContinue]);

  const onContinue = useCallback(() => {
    if (showTerms) {
      if (!termsAccepted) return;
      // update user terms and privacy accepted
      dispatch(
        actions.user.update({
          user: {
            termsAndPrivacyAccepted: true,
            marketingEmails,
            marketingSms: marketingEmails ? true : false,
            transactionalEmails: true,
            transactionalSms: true,
          },
          noToast: true,
        })
      );
    }
    if (showMobile && phoneNumber?.length) {
      if (showVerificationStep) {
        verifyCodeAndContinue();
      } else {
        sendVerificationCode();
      }
    }
  }, [
    showTerms,
    termsAccepted,
    showMobile,
    phoneNumber?.length,
    dispatch,
    showVerificationStep,
    verifyCodeAndContinue,
    sendVerificationCode,
    marketingEmails,
  ]);

  const resendCode = useCallback(() => {
    sendVerificationCode();
  }, [sendVerificationCode]);

  const isDisabled = useMemo(() => {
    return (
      isLoading ||
      (showTerms && !termsAccepted) ||
      (showMobile && !phoneNumber?.length) ||
      (showMobile && showVerificationStep && !verificationCode?.length)
    );
  }, [
    isLoading,
    phoneNumber,
    showMobile,
    showTerms,
    showVerificationStep,
    termsAccepted,
    verificationCode?.length,
  ]);

  useEffect(() => {
    // Check if we have a pending verification
    const storedPhone = localStorage.getItem('pendingPhoneVerification');
    if (storedPhone) {
      setPhoneNumber(storedPhone);
      setShowVerificationStep(true);
    }
  }, []);

  useEffect(() => {
    if (user?.phone) {
      setPhoneNumber(user.phone);
      window.sessionStorage.removeItem(COLLECT_PHONE_NUMBER_COOKIE);
      const prematterId = window.sessionStorage.getItem(PREMATTER_ID_COOKIE);
      if (prematterId) {
        window.sessionStorage.removeItem(PREMATTER_ID_COOKIE);
        if (!showTerms) {
          window.location.href = `/case/${prematterId}`;
        }
      } else {
        window.sessionStorage.removeItem(PREMATTER_ID_COOKIE);
        if (!showTerms) {
          window.location.href = '/account';
        }
      }
    }
  }, [user.phone, setPhoneNumber, showTerms]);

  useEffect(() => {
    modalRef.current?.openModal();
  }, [modalRef]);

  // on enter press and not disabled we trigger the onContinue
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !isDisabled) {
        onContinue();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDisabled, onContinue]);

  useEffect(() => {
    if (prefillTerms) {
      setTermsAccepted(true);
    }
  }, [prefillTerms]);

  return (
    <Modal
      ref={modalRef}
      primaryButton={{
        label: t('confirm.continue'),
        onClick: onContinue,
        disabled: isDisabled,
      }}
      useAuthStyle={true}
      hideCloseButton={true}
      fullscreen={true}
      title={getAuthBodyTitle({
        showPhoneNumber: showMobile,
        showVerificationStep: showVerificationStep,
        t,
      })}
      description={getAuthBodyDescription({
        showPhoneNumber: showMobile,
        showVerificationStep: showVerificationStep,
        t,
      })}
      footerText={
        <AuthBodyFooter
          showMobile={showMobile}
          showVerificationStep={showVerificationStep}
          resendCode={resendCode}
        />
      }
    >
      <AuthModalBodyContent
        showPhoneNumber={showMobile}
        showVerificationStep={showVerificationStep}
        verificationCode={verificationCode}
        setVerificationCode={setVerificationCode}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        isLoading={isLoading}
        setTermsAccepted={setTermsAccepted}
        termsAccepted={termsAccepted}
        showTerms={showTerms}
        marketingEmails={marketingEmails}
        setMarketingEmails={setMarketingEmails}
      />
    </Modal>
  );
};
